import { defaultFormConfig, type FormConfig } from '@cod/config-schema';

/**
 * The Dashboard's pure model. Separate from `Dashboard.tsx` because that module pulls in Nimbus
 * and the Nexo-backed api client, which need a `window`.
 */

/**
 * The config keys the Form Designer owns. Everything else in the document belongs to another
 * screen — shipping rates, quantity offers, tracking and the thank-you/cart settings each have
 * their own checklist step, so editing one of those must not tick "Customize your form". The
 * server-derived keys (`version`, `visibility`, `store_country`, `storefront_runtime`) are
 * excluded for the same reason: no merchant authored them.
 */
const DESIGNER_KEYS = [
  'mode',
  'enabled',
  'header',
  'button',
  'appearance',
  'fields_style',
  'fields',
  'summary',
  'discount',
  'product_card',
  'totals_style',
  'shipping_method',
  'coverage',
  'consent',
  'submit',
  'messages',
] as const satisfies readonly (keyof FormConfig)[];

/**
 * Structural equality, ignoring object key order. Needed because the two sides come from
 * different places — the stored config round-trips through zod (key order follows the schema),
 * the baseline is an object literal — so `JSON.stringify` would report a difference that is not
 * one.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => deepEqual(item, b[i]));
  }
  const aKeys = Object.keys(a as object);
  const bKeys = Object.keys(b as object);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    (k) =>
      Object.prototype.hasOwnProperty.call(b, k) &&
      deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}

/** Has the merchant changed anything the Form Designer owns, versus the config seeded at install? */
export function isFormCustomized(config: FormConfig): boolean {
  return DESIGNER_KEYS.some((k) => !deepEqual(config[k], defaultFormConfig[k]));
}

/**
 * Pixels that would actually fire: the master tracking switch is on, the pixel is enabled and it
 * has an id. Mirrors the backend's `publicTrackingView` filter, plus the master switch — a pixel
 * behind a switched-off master is configured, not active.
 */
export function activePixelCount(config: FormConfig): number {
  if (!config.tracking.enabled) return 0;
  return config.tracking.pixels.filter((p) => p.enabled !== false && p.pixel_id).length;
}

/**
 * Shipping rates beyond the single free-shipping rate seeded at install. The seeded rate is not a
 * merchant decision, so a store that still has only it has not set up shipping.
 */
export function hasCustomShippingRates(config: FormConfig): boolean {
  return !deepEqual(config.shipping_rates, defaultFormConfig.shipping_rates);
}

/**
 * Short date + time for an order row. `toLocaleString` renders the full "8/13/2026, 7:39:22 AM",
 * whose seconds carry no meaning here and push the row wide enough to crowd the total.
 *
 * `hourCycle: 'h23'` rather than `hour12: false`, which asks for "not 12-hour" and leaves the engine
 * to pick between the 00–23 clock and the `h24` one that writes midnight as `24:10`. Current engines
 * resolve it to `h23` — the spec was amended to say so — but naming the cycle we want costs nothing
 * and does not depend on that. Passing `hour12` as well would make `hourCycle` be ignored, so it
 * stays absent.
 *
 * Fixed rather than left to the locale because the order list is a log: `01:38 PM` in a stack of
 * rows is harder to scan than `13:38`, and an `es`/`pt-BR` merchant reading an `en`-formatted admin
 * would otherwise get a 12-hour clock their own locale never uses.
 */
export function formatOrderTime(iso: string, locale: string): string {
  return new Date(iso).toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
}
